import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { HandCoins, Check, X, PlayCircle, Eye } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Loader, Card, EmptyState } from "@/components/kit";
import { formatCOP, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [
  { id: "PENDIENTE", label: "Pendientes" },
  { id: "EN_PROCESO", label: "En proceso" },
  { id: "PAGADA", label: "Pagados" },
  { id: "RECHAZADA", label: "Rechazados" },
];
const BADGE = {
  PENDIENTE: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  EN_PROCESO: "text-blue-400 border-blue-500/40 bg-blue-500/10",
  PAGADA: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  RECHAZADA: "text-red-400 border-red-500/40 bg-red-500/10",
};

export default function AdminDriverPayouts() {
  const [tab, setTab] = useState("PENDIENTE");
  const [rows, setRows] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [driver, setDriver] = useState("all");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    rpc("admin_get_payout_requests", {
      p_status: tab, p_driver: driver === "all" ? null : driver,
      p_from: fromDate ? new Date(fromDate).toISOString() : null, p_to: null,
    }).then((d) => {
      setRows(d || []);
      const uniq = {};
      (d || []).forEach((r) => { uniq[r.driver_id] = r.driver_name; });
      setDrivers((prev) => {
        const merged = { ...Object.fromEntries(prev.map((x) => [x.id, x.name])), ...uniq };
        return Object.entries(merged).map(([id, name]) => ({ id, name }));
      });
    }).catch(() => setRows([]));
  }, [tab, driver, fromDate]);
  useEffect(() => { load(); }, [load]);

  const setProcessing = async (r) => {
    try { await rpc("admin_set_payout_processing", { p_id: r.id }); toast.success("Marcada EN PROCESO"); load(); }
    catch (e) { toast.error(e?.message || "Error"); }
  };

  return (
    <div>
      <PageHeader title="Pagos a conductores" subtitle="Solicitudes de cobro por saldo. Único lugar donde se confirma el pago efectivo." testId="admin-driver-payouts" />

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`payout-tab-${t.id}`}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#FF6A00] text-black" : "border border-[#27272A] text-gray-300 hover:bg-[#18181B]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mb-5 flex flex-wrap gap-3">
        <Select value={driver} onValueChange={setDriver}>
          <SelectTrigger className="w-56 border-[#27272A] bg-[#0d0d0f]" data-testid="filter-driver"><SelectValue placeholder="Conductor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los conductores</SelectItem>
            {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-44 border-[#27272A] bg-[#0d0d0f]" data-testid="filter-from" />
        {fromDate && <Button variant="outline" className="border-[#27272A]" onClick={() => setFromDate("")} data-testid="filter-clear">Limpiar fecha</Button>}
      </div>

      {rows === null ? <Loader /> : rows.length === 0 ? (
        <EmptyState icon={HandCoins} title="Sin solicitudes" subtitle={`No hay solicitudes en estado ${tab}.`} testId="payouts-empty" />
      ) : (
        <div className="space-y-3" data-testid="payout-requests-list">
          {rows.map((r) => (
            <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`payout-request-${r.id}`}>
              <div>
                <div className="font-heading text-lg font-bold text-white">{r.driver_name}</div>
                <div className="text-xs text-gray-500">
                  {formatDateTime(r.requested_at)} · {r.method}
                  {r.account_number !== "••••" && ` · ${r.account_number}`}
                  {r.phone_number !== "••••" && ` · ${r.phone_number}`}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="font-heading text-xl font-extrabold text-[#FF6A00]">{formatCOP(r.amount)}</div>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${BADGE[r.status]}`}>{r.status}</span>
                </div>
                <Button size="sm" variant="outline" className="border-[#27272A] text-gray-300" onClick={() => setDetail(r)} data-testid={`payout-detail-${r.id}`}><Eye size={14} /></Button>
                {r.status === "PENDIENTE" && (
                  <Button size="sm" className="bg-blue-500/90 text-white hover:bg-blue-600" onClick={() => setProcessing(r)} data-testid={`payout-process-${r.id}`}><PlayCircle size={14} className="mr-1" />Procesar</Button>
                )}
                {(r.status === "PENDIENTE" || r.status === "EN_PROCESO") && (
                  <>
                    <Button size="sm" className="bg-emerald-500/90 text-white hover:bg-emerald-600" onClick={() => setConfirmTarget(r)} data-testid={`payout-confirm-${r.id}`}><Check size={14} className="mr-1" />Pagar</Button>
                    <Button size="sm" className="bg-red-500/90 text-white hover:bg-red-600" onClick={() => setRejectTarget(r)} data-testid={`payout-reject-${r.id}`}><X size={14} /></Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog target={confirmTarget} onClose={() => setConfirmTarget(null)} onDone={load} />
      <RejectDialog target={rejectTarget} onClose={() => setRejectTarget(null)} onDone={load} />
      <DetailDialog target={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function ConfirmDialog({ target, onClose, onDone }) {
  const [method, setMethod] = useState("NEQUI");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setMethod(target.method || "NEQUI"); setReference(""); } }, [target]);
  const submit = async () => {
    setBusy(true);
    try {
      const res = await rpc("admin_confirm_payout", { p_id: target.id, p_method: method, p_reference: reference || null, p_paid_at: null });
      toast.success(`Pago confirmado. Asignado a servicios: ${formatCOP(res.asignado)}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.message || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="confirm-payout-dialog">
        <DialogHeader>
          <DialogTitle>Confirmar pago de {target && formatCOP(target.amount)}</DialogTitle>
          <DialogDescription>Solo confírmalo cuando hayas realizado efectivamente la transferencia/entrega. Quedará registrado con tu usuario y la fecha.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="border-[#27272A] bg-[#0d0d0f]" data-testid="confirm-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NEQUI">Nequi</SelectItem>
              <SelectItem value="BANCOLOMBIA">Bancolombia</SelectItem>
              <SelectItem value="CASH">Efectivo</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Referencia / comprobante (opcional)" value={reference} onChange={(e) => setReference(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="confirm-reference" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="confirm-submit">Confirmar pago realizado</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setReason(""); }, [target]);
  const submit = async () => {
    setBusy(true);
    try { await rpc("admin_reject_payout", { p_id: target.id, p_reason: reason.trim() }); toast.success("Solicitud rechazada; saldo liberado"); onDone(); onClose(); }
    catch (e) { toast.error(e?.message || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="reject-payout-dialog">
        <DialogHeader>
          <DialogTitle>Rechazar solicitud</DialogTitle>
          <DialogDescription>El motivo es obligatorio. El saldo vuelve a quedar disponible para el conductor.</DialogDescription>
        </DialogHeader>
        <Textarea placeholder="Motivo del rechazo (obligatorio)" value={reason} onChange={(e) => setReason(e.target.value)} className="border-[#27272A] bg-[#0d0d0f]" data-testid="reject-reason" />
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !reason.trim()} className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-40" data-testid="reject-submit">Rechazar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ target, onClose }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="payout-detail-dialog">
        {target && (
          <>
            <DialogHeader><DialogTitle>Solicitud de {target.driver_name}</DialogTitle>
              <DialogDescription>Datos de recepción del pago (enmascarados).</DialogDescription></DialogHeader>
            <div className="space-y-2 text-sm">
              <Row k="Monto" v={formatCOP(target.amount)} />
              <Row k="Método" v={target.method} />
              <Row k="Titular" v={target.holder_name || "—"} />
              <Row k="Cédula" v={target.id_number} />
              {target.account_number !== "••••" && <Row k="Cuenta" v={target.account_number} />}
              {target.phone_number !== "••••" && <Row k="Celular" v={target.phone_number} />}
              <Row k="Estado" v={target.status} />
              <Row k="Solicitada" v={formatDateTime(target.requested_at)} />
              {target.decided_by_name && <Row k="Gestionada por" v={`${target.decided_by_name} · ${formatDateTime(target.decided_at)}`} />}
              {target.paid_at && <Row k="Pagada" v={formatDateTime(target.paid_at)} />}
              {target.reference && <Row k="Referencia" v={target.reference} />}
              {target.reject_reason && <Row k="Motivo rechazo" v={target.reject_reason} />}
              <Row k="Saldo disponible actual" v={formatCOP(target.driver_available_now)} />
              <Row k="Servicios cubiertos" v={String(target.allocations)} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-[#1e1e21] py-1.5"><span className="text-gray-500">{k}</span><span className="text-white">{v}</span></div>
);
